import React, { useState, useRef } from 'react';
import { Button, Input, Select, Space, Typography } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { AsYouType } from 'libphonenumber-js';
import firebase from 'firebase/app';

import {
  COUNTRIES, getPlaceholder, parseE164, nationalFromE164, formatE164ForDisplay, defaultCountry,
} from '../../utils/phone';
import { phoneAuthErrorMessage } from '../../utils/identity';
import { trackSmsVerificationRequested, trackSmsVerificationCompleted } from '../../utils/smsTracking';

const { Text } = Typography;
const { Option } = Select;

/**
 * Phone capture/verify for the signup-completion and profile-edit flows.
 *
 * A phone number must NEVER be persisted unverified, so instead of a free-text
 * field we OTP-verify it and link it to the already signed-in user
 * (linkWithCredential) — exactly the AddLoginMethodSection pattern. Once linked,
 * the parent form re-reads currentUser.phoneNumber and shows the locked,
 * verified field.
 *
 * initialPhone (E.164): pre-fills an existing on-file number (e.g. a legacy number
 * added as a free-text contact field before verification existed) so the user can
 * verify it in one tap instead of retyping.
 *
 * onVerified(e164) is called after the number is linked.
 */
export default function SignupPhoneVerify({ onVerified, initialPhone, profileCountry }) {
  const seed = initialPhone ? nationalFromE164(initialPhone) : null;
  const [country, setCountry] = useState(
    (seed && COUNTRIES.find((c) => c.code === seed.country)) || defaultCountry(profileCountry),
  );
  const [raw, setRaw] = useState(seed ? seed.national : '');
  const [step, setStep] = useState('idle'); // 'idle' | 'verify'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const confirmationRef = useRef(null);
  const recaptchaRef = useRef(null);

  const e164 = parseE164(raw, country.code);

  const handleChange = (e) => {
    const formatter = new AsYouType(country.code);
    setRaw(formatter.input(e.target.value));
    setError(null);
  };

  const sendCode = async () => {
    if (!e164) { setError('Enter a valid mobile number'); return; }
    setError(null);
    setLoading(true);
    try {
      const container = recaptchaRef.current;
      if (!container) throw new Error('reCAPTCHA container not ready');
      container.innerHTML = '';
      const verifier = new firebase.auth.RecaptchaVerifier(container, { size: 'normal' });
      const result = await firebase.auth().signInWithPhoneNumber(e164, verifier);
      confirmationRef.current = result;
      trackSmsVerificationRequested({
        e164, inputCountry: country.code, profileCountry, context: 'signup',
      });
      setStep('verify');
    } catch (err) {
      setError(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (code.trim().length !== 6 || !confirmationRef.current) return;
    setError(null);
    setLoading(true);
    try {
      const credential = firebase.auth.PhoneAuthProvider.credential(
        confirmationRef.current.verificationId,
        code.trim(),
      );
      const user = firebase.auth().currentUser;
      if (user) await user.linkWithCredential(credential);
      else await firebase.auth().signInWithCredential(credential);
      try { await firebase.auth().currentUser?.reload?.(); } catch (_) { /* noop */ }
      const num = firebase.auth().currentUser?.phoneNumber || e164;
      trackSmsVerificationCompleted({ e164, inputCountry: country.code, profileCountry, context: 'signup' });
      if (onVerified) onVerified(num);
    } catch (err) {
      setError(phoneAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {initialPhone
          ? 'This mobile is on your profile but isn’t verified for sign-in yet. We’ll text a code to verify it and secure your account.'
          : 'Add a mobile number you can also sign in with. We’ll text a code to verify it.'}
      </Text>

      {step === 'idle' && (
        <>
          <Input.Group compact style={{ display: 'flex' }}>
            <Select
              value={country.code}
              onChange={(c) => setCountry(COUNTRIES.find((x) => x.code === c) || COUNTRIES[0])}
              style={{ width: 110, flexShrink: 0 }}
              dropdownMatchSelectWidth={false}
              disabled={loading}
            >
              {COUNTRIES.map((c) => (
                <Option key={c.code} value={c.code}>{c.flag} {c.dial}</Option>
              ))}
            </Select>
            <Input
              placeholder={getPlaceholder(country.code)}
              value={raw}
              onChange={handleChange}
              disabled={loading}
              style={{ flex: 1 }}
            />
          </Input.Group>
          {/* reCAPTCHA renders here */}
          <div ref={recaptchaRef} />
          <Button onClick={sendCode} loading={loading} disabled={!e164}>
            Send verification code
          </Button>
        </>
      )}

      {step === 'verify' && (
        <>
          <Input
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            inputMode="numeric"
          />
          <Space>
            <Button type="primary" onClick={verify} loading={loading} disabled={code.length !== 6}>
              Verify
            </Button>
            <Button
              type="link"
              onClick={() => { setStep('idle'); setCode(''); confirmationRef.current = null; }}
              disabled={loading}
            >
              Change number
            </Button>
          </Space>
        </>
      )}

      {step === 'verify' && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          <CheckCircleOutlined /> Code sent to {formatE164ForDisplay(e164)}
        </Text>
      )}

      {error && <Text type="danger" style={{ fontSize: 12 }}>{error}</Text>}
    </Space>
  );
}
